import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";
import FlightLand from "@mui/icons-material/FlightLand";
import FlightTakeoff from "@mui/icons-material/FlightTakeoff";
import { IFlight } from "redux/searchForm";
import { useTranslations } from "hooks/useTranslations";
import { useFetchAirportsQuery } from "redux/services/airportSearch";
import "./airportSearch.scss";
import { Sampler } from "../../../Sampler/Sampler";
import { deepCloneObject, useConfig } from "@thekilroygroup/shared-ui";
import { getAirportDetails } from "../../../../utils/flightDetailApiUtils";
import { decodeHtmlEntities } from "../../../../utils/helpers";

export interface ILocation {
  cityName: string;
  code: string;
  country: string;
  locationName: string;
  type: string;
}

interface IProps {
  fromOrTo: "departure" | "arrival";
  label: string;
  noOptionsText: string;
  index: number;
  iata?: any;
  flights: IFlight[];
  onChange: (
    option: ILocation | null,
    index: number,
    fromOrTo: "departure" | "arrival",
  ) => void;
  onBlur: (flights: IFlight[]) => void;
}

export default function AirportSearch(props: IProps) {
  const {
    fromOrTo,
    label,
    noOptionsText,
    index,
    iata,
    flights,
    onChange,
    onBlur,
  } = props;

  const { appConfig } = useConfig();
  const { translations } = useTranslations();

  const [query, setQuery] = useState("");
  const [airport, setAirport] = useState<ILocation | null>(null);
  const [samplerActive, setSamplerActive] = useState(false);
  const [isFetchingLocations, setIsFetchingLocations] = useState(false);

  const { data: dataIataLookup, isFetching: isFetchingIata } =
    getAirportDetails(iata);

  useEffect(() => {
    if (airport?.code !== iata && dataIataLookup !== null) {
      setAirport(dataIataLookup);
    }
  }, [dataIataLookup]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "endangeredspecies") {
      setSamplerActive(true);
    }
    setQuery(encodeURI(event.target.value));
  };

  const fromToIcon = () => {
    if (fromOrTo === "departure") return <FlightTakeoff />;
    if (fromOrTo === "arrival") return <FlightLand />;
  };

  const handleSelect = (values: ILocation | null) => {
    setAirport(values);
    onChange(values, index, fromOrTo);
  };

  const validate = () => {
    let nextFlights: IFlight[] = deepCloneObject(flights);

    let nextValidation = {
      ...flights[index].validation,
      [fromOrTo]: {
        dirty: true,
        valid: flights[index][fromOrTo] !== null,
      },
    };

    nextFlights[index].validation = nextValidation;
    onBlur(nextFlights);
  };

  const getValidState = () => {
    return (
      flights[index].validation[fromOrTo].dirty &&
      !flights[index].validation[fromOrTo].valid
    );
  };

  const getErrorText = () => {
    if (getValidState()) {
      return translations.search.fromTo[fromOrTo].errorText;
    }
    return "";
  };

  const getDisabled = (option: ILocation) => {
    if (fromOrTo === "arrival" && option.code === flights[index].departure)
      return true;
    else if (fromOrTo === "departure" && option.code === flights[index].arrival)
      return true;
    return false;
  };

  const getRenderOption = (option: ILocation) => {
    let str = "";
    option.cityName && (str += decodeHtmlEntities(option.cityName) + ", ");
    option.code && (str += "[" + option.code + "], ");
    option.locationName &&
      (str += decodeHtmlEntities(option.locationName) + ", ");
    option.country && (str += decodeHtmlEntities(option.country));

    return str;
  };

  const getOptions = (str: string) => {
    const { data, isFetching, isSuccess, originalArgs } = useFetchAirportsQuery(
      {
        query: decodeHtmlEntities(str),
        limit: 20,
        config: appConfig,
      },
      {
        skip: str.length <= 2,
      },
    );

    if (isFetching && originalArgs?.query?.length > 2 && !isFetchingLocations) {
      setIsFetchingLocations(true);
    } else if (!isFetching && isFetchingLocations) {
      setIsFetchingLocations(false);
    }

    if (isFetching === false && isSuccess) {
      return data;
    } else {
      return [];
    }
  };
  return (
    <>
      <Autocomplete
        loading={isFetchingLocations}
        loadingText={
          <Box sx={{ width: "100%", display: "flex" }}>
            <CircularProgress
              size="24px"
              color="primary"
              sx={{ m: "auto", width: 1 }}
            />
          </Box>
        }
        freeSolo
        clearOnBlur
        noOptionsText={noOptionsText}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        popupIcon={""}
        options={getOptions(query)}
        getOptionLabel={(option: any) => getRenderOption(option)}
        getOptionDisabled={(option) => getDisabled(option)}
        value={airport || null}
        sx={sxStyles}
        onChange={(_, obj) => {
          handleSelect(obj as ILocation | null);
        }}
        filterOptions={(x) => x}
        onBlur={() => validate}
        renderInput={(params) => (
          <TextField
            autoComplete="off"
            onChange={handleInputChange}
            {...params}
            label={label}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">{fromToIcon()}</InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {isFetchingIata && !airport ? (
                    <CircularProgress size="24px" color="primary" />
                  ) : (
                    params.InputProps.endAdornment
                  )}
                </InputAdornment>
              ),
            }}
            variant="outlined"
            color="secondary"
            error={getValidState()}
            helperText={getErrorText()}
          />
        )}
      />
      {/* @ts-ignore */}
      <div easter-egg="search-for:endangeredspecies">
        {samplerActive && (
          <>
            <Sampler
              kill={() => {
                setSamplerActive(false);
              }}
            />
          </>
        )}
      </div>
    </>
  );
}

const sxStyles = {
  width: 1,
  my: 1,
  "&:first-of-type": {
    mr: { md: 5 },
  },
  "& .MuiOutlinedInput-root": {
    pr: "30px!important",
  },
};
